import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Clock,
  Users,
  Coffee,
  Phone,
  MapPin,
  Mail,
  Star,
  ChefHat,
  Truck,
  Shield,
  Award,
  UtensilsCrossed,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import heroFoodLeft from "@/assets/hero-food-left.jpg";
import heroFoodRight from "@/assets/hero-food-right.jpg";
import restaurantChef from "@/assets/restaurant-chef.jpg";
import restaurantInterior from "@/assets/restaurant-interior.jpg";
import restaurantPasta from "@/assets/restaurant-pasta.jpg";
import restaurantIngredients from "@/assets/restaurant-ingredients.jpg";
import restaurantDiningRoom from "@/assets/restaurant-dining-room.jpg";
import restaurantFoodSpread from "@/assets/restaurant-food-spread.jpg";
import restaurantSalmonPlate from "@/assets/restaurant-salmon-plate.jpg";
import restaurantPastaBowl from "@/assets/restaurant-pasta-bowl.jpg";
import restaurantHerbs from "@/assets/restaurant-herbs.jpg";
// Restaurant Jungfrau logo is in public/images, so we'll use the public path
import menuBoiledEgg from "@/assets/menu-boiled-egg.jpg";
import menuChickenBurger from "@/assets/menu-chicken-burger.jpg";
import menuSpicyChips from "@/assets/menu-spicy-chips.jpg";
import dishChickenBurger from "@/assets/dish-chicken-burger.jpg";
import dishChickenRice from "@/assets/dish-chicken-rice.jpg";
import dishAvocadoToast from "@/assets/dish-avocado-toast.jpg";
import dishSpicyPlatter from "@/assets/dish-spicy-platter.jpg";
import dishAppetizer from "@/assets/dish-appetizer.jpg";
import testimonialFoodLeft from "@/assets/testimonial-food-left.jpg";
import testimonialFoodRight from "@/assets/testimonial-food-right.jpg";
import Footer from "@/components/Footer";
import ContactConciergeModal from "@/components/ContactConciergeModal";
import NewHeader from "@/components/NewHeader";
import AutoScrollGallery from "@/components/ui/auto-scroll-gallery";
import SEO from "@/components/SEO";
import { getPageSEO, SEOLanguage } from "@/data/seoTranslations";
import { getTranslatedStructuredData } from "@/data/structuredDataTranslations";
import { createBreadcrumbSchema } from "@/data/structuredData";
import RatingDisplay from "@/components/RatingDisplay";
import { trackContactAction } from "@/utils/contactTracking";

const Restaurant = () => {
  const { t, language } = useLanguage();
  const seoLang = language as SEOLanguage;
  const seoData = getPageSEO("restaurant", seoLang);
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: "Home", url: "https://beatenberg-resortspa.ch/" },
    { name: "Restaurant", url: "https://beatenberg-resortspa.ch/dining" },
  ]);

  const [activeTab, setActiveTab] = useState("five_course_menu");
  const [scrollY, setScrollY] = useState(0);
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const imageRef = useRef(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isInHeroSection, setIsInHeroSection] = useState(true);
  const heroSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Detect if user is in hero section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInHeroSection(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: "-100px 0px 0px 0px", // Account for header height
      },
    );

    if (heroSectionRef.current) {
      observer.observe(heroSectionRef.current);
    }

    return () => {
      if (heroSectionRef.current) {
        observer.unobserve(heroSectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsImageVisible(true);
        }
      },
      {
        threshold: 0.3, // Trigger when 30% of the element is visible
        rootMargin: "0px 0px -10% 0px", // Start animation slightly before element is fully in view
      },
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  // Safari video autoplay fix - Programmatic play trigger
  useEffect(() => {
    const playVideo = async () => {
      if (heroVideoRef.current) {
        try {
          await heroVideoRef.current.play();
        } catch (error) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try to play when component mounts
    playVideo();

    // Also try when video metadata is loaded
    const video = heroVideoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", playVideo);
      return () => video.removeEventListener("loadedmetadata", playVideo);
    }
  }, []);

  // Slider navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % diningVenues.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + diningVenues.length) % diningVenues.length,
    );
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const menuItems = {
    five_course_menu: [
      {
        name: t("restaurant.menuItems.fiveCourseMenu.name"),
        price: "CHF 55.00",
        image: "/images/5-course-menu.jpg",
        rating: 5,
        description: t("restaurant.menuItems.fiveCourseMenu.description"),
      },
    ],

    sides: [
      {
        name: t("restaurant.menuItems.smokedSalmonSandwich.name"),
        price: "CHF 6.50",
        image: restaurantSalmonPlate,
        rating: 4,
        description: t("restaurant.menuItems.smokedSalmonSandwich.description"),
      },
      {
        name: t("restaurant.menuItems.eggCheeseSandwich.name"),
        price: "CHF 6.50",
        image: "/images/popular dishes/Jungfrau Classic.jpg",
        rating: 4,
        description: t("restaurant.menuItems.eggCheeseSandwich.description"),
      },
      {
        name: t("restaurant.menuItems.healthySaladWithEgg.name"),
        price: "CHF 7.50",
        image: "/images/menu/Gesunder_Schweizer_Salat.webp",
        rating: 5,
        description: t("restaurant.menuItems.healthySaladWithEgg.description"),
      },
      {
        name: t("restaurant.menuItems.healthySalad.name"),
        price: "CHF 7.50",
        image: "/images/menu/Gesunder_Schweizer_Salat.webp",
        rating: 4,
        description: t("restaurant.menuItems.healthySalad.description"),
      },
      {
        name: t("restaurant.menuItems.chickenNuggets.name"),
        price: "CHF 10.00",
        image: "/images/menu/falafel.webp",
        rating: 4,
        description: t("restaurant.menuItems.chickenNuggets.description"),
      },
      {
        name: t("restaurant.menuItems.frenchFries.name"),
        price: "CHF 9.00",
        image: "/images/menu/french_fries.webp",
        rating: 4,
        description: t("restaurant.menuItems.frenchFries.description"),
      },
      {
        name: t("restaurant.menuItems.vegetableSoup.name"),
        price: "CHF 5.00",
        image: "/images/menu/Gemuesesuppe_des_Tages.webp",
        rating: 5,
        description: t("restaurant.menuItems.vegetableSoup.description"),
      },
      {
        name: t("restaurant.menuItems.miniCroissant.name"),
        price: "CHF 4.50",
        image: "/images/menu/mini_croissant.jpg",
        rating: 4,
        description: t("restaurant.menuItems.miniCroissant.description"),
      },
    ],

    beverages: [
      {
        name: t("restaurant.menuItems.clavienCornalin.name"),
        price: "CHF 11.50",
        image: "/images/menu/cornalin.webp",
        rating: 5,
        description: t("restaurant.menuItems.clavienCornalin.description"),
      },
      {
        name: t("restaurant.menuItems.yvorneRouge.name"),
        price: "CHF 9.50",
        image: "/images/menu/yvorne_rouge.webp",
        rating: 5,
        description: t("restaurant.menuItems.yvorneRouge.description"),
      },
      {
        name: t("restaurant.menuItems.yvorneBlanc.name"),
        price: "CHF 9.50",
        image: "/images/menu/yvorne_blanc.webp",
        rating: 5,
        description: t("restaurant.menuItems.yvorneBlanc.description"),
      },
      {
        name: t("restaurant.menuItems.rugenbrauAmber.name"),
        price: "CHF 6.50",
        image: "/images/menu/rugenbrau_amber.webp",
        rating: 5,
        description: t("restaurant.menuItems.rugenbrauAmber.description"),
      },
      {
        name: t("restaurant.menuItems.rugenbrauSpezial.name"),
        price: "CHF 6.50",
        image: "/images/menu/rugenbrau_spezial.webp",
        rating: 5,
        description: t("restaurant.menuItems.rugenbrauSpezial.description"),
      },
      {
        name: t("restaurant.menuItems.rivella.name"),
        price: "CHF 5.00",
        image: "/images/menu/rivella.webp",
        rating: 5,
        description: t("restaurant.menuItems.rivella.description"),
      },
      {
        name: t("restaurant.menuItems.iceTea.name"),
        price: "CHF 5.00",
        image: "/images/menu/ice_tea.webp",
        rating: 5,
        description: t("restaurant.menuItems.iceTea.description"),
      },
      {
        name: t("restaurant.menuItems.apfelschorle.name"),
        price: "CHF 5.00",
        image: "/images/menu/apfelschorle.webp",
        rating: 5,
        description: t("restaurant.menuItems.apfelschorle.description"),
      },
    ],
  };

  const featuredItems = [
    {
      name: t("restaurant.menuItems.smokedSalmonSandwich.name"),
      price: "CHF 6.50",
      ingredients: t("restaurant.menuItems.smokedSalmonSandwich.description"),
      image: restaurantSalmonPlate,
    },
    {
      name: t("restaurant.menuItems.healthySaladWithEgg.name"),
      price: "CHF 7.50",
      ingredients: t("restaurant.menuItems.healthySaladWithEgg.description"),
      image: "/images/menu/Gesunder_Schweizer_Salat.webp",
    },
    {
      name: t("restaurant.menuItems.clavienCornalin.name"),
      price: "CHF 11.50",
      ingredients: t("restaurant.menuItems.clavienCornalin.description"),
      image: "/images/menu/cornalin.webp",
    },
    {
      name: t("restaurant.menuItems.rugenbrauAmber.name"),
      price: "CHF 6.50",
      ingredients: t("restaurant.menuItems.rugenbrauAmber.description"),
      image: "/images/menu/rugenbrau_amber.webp",
    },
    {
      name: t("restaurant.menuItems.frenchFries.name"),
      price: "CHF 9.00",
      ingredients: t("restaurant.menuItems.frenchFries.description"),
      image: "/images/menu/french_fries.webp",
    },
  ];

  const testimonials = [
    {
      id: 1,
      review:
        "Today we had dinner at the Jungfrau restaurant. We were greeted in a friendly manner by a member of the kitchen staff, who apologised that there were few staff on site today and that he would therefore be taking over the service. We were served quickly, competently, friendly and attentively, even though he alone had to manage the service for the 30 or so guests; compliments! We opted for salad or soup and then various pinsa. Everything was delicious and beautifully presented. The pinsa were rather large, perfect for a big appetite, but they were too big for our appetite. We'd love to go again!",
      author: "Tobias A. Merz",
      image: "/images/For Testimonials/IMG-20250812-WA0041.jpg",
    },
    {
      id: 2,
      review:
        "I had a wonderful experience at the hotel. Every staff member I encountered, from the valet to the check-in to the cleaning staff were delightful and eager to help! Tim, in particular, was such a welcoming person and helped us a lot during our stay. The rooms were clean, very comfortable, and the internet speed was awesome. The cliff view rooms are a must-stay for anyone visiting the area. And don't miss the buffet - it's amazing!",
      author: "Clement Anto",
      image: "/images/For Testimonials/Agustina Kraitz.webp",
    },
    {
      id: 3,
      review:
        "Good Communication and the food was Great. The facilities were good. Loved the deserts and the way of presenting. We came here for a family dinner and the place has won my heart",
      author: "Leon Weber",
      image: "/images/For Testimonials/Amar Nanavati.webp",
    },
  ];

  const diningVenues = [
    {
      id: "main-restaurant",
      title: t("restaurant.diningVenues.mainRestaurant.title"),
      subtitle: t("restaurant.diningVenues.mainRestaurant.subtitle"),
      description: t("restaurant.diningVenues.mainRestaurant.description"),
      features: t("restaurant.diningVenues.mainRestaurant.features"),
      perfectFor: t("restaurant.diningVenues.mainRestaurant.perfectFor"),
      bgImage: "/images/main_restaurant.png",
      gradient: "from-orange-400 to-orange-600",
      accentColor: "orange-500",
      icon: "🍽️",
    },
    {
      id: "terrace-dining",
      title: t("restaurant.diningVenues.terraceDining.title"),
      subtitle: t("restaurant.diningVenues.terraceDining.subtitle"),
      description: t("restaurant.diningVenues.terraceDining.description"),
      features: t("restaurant.diningVenues.terraceDining.features"),
      perfectFor: t("restaurant.diningVenues.terraceDining.perfectFor"),
      bgImage: "/images/alpine_terrace.jpg",
      gradient: "from-blue-400 to-blue-600",
      accentColor: "blue-500",
      icon: "🏔️",
    },
    {
      id: "outdoor-table",
      title: t("restaurant.diningVenues.outdoorTable.title"),
      subtitle: t("restaurant.diningVenues.outdoorTable.subtitle"),
      description: t("restaurant.diningVenues.outdoorTable.description"),
      features: t("restaurant.diningVenues.outdoorTable.features"),
      perfectFor: t("restaurant.diningVenues.outdoorTable.perfectFor"),
      bgImage: "/images/main_restauraunt_2.png",
      gradient: "from-green-400 to-green-600",
      accentColor: "green-500",
      icon: "🌿",
    },
    {
      id: "alpine-breakfast",
      title: t("restaurant.diningVenues.alpineBreakfast.title"),
      subtitle: t("restaurant.diningVenues.alpineBreakfast.subtitle"),
      description: t("restaurant.diningVenues.alpineBreakfast.description"),
      features: t("restaurant.diningVenues.alpineBreakfast.features"),
      perfectFor: t("restaurant.diningVenues.alpineBreakfast.perfectFor"),
      bgImage: "/images/breakfast_buffet.jpg",
      gradient: "from-purple-400 to-purple-600",
      accentColor: "purple-500",
      icon: "🌅",
    },
  ];

  return (
    <div
      className="min-h-screen bg-background overflow-x-hidden"
      data-oid="4zkb_:y"
    >
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical="/dining"
        language={seoLang}
        structuredData={[
          getTranslatedStructuredData.restaurant(seoLang),
          breadcrumbSchema,
        ]}
        data-oid="gcka1q9"
      />

      <NewHeader hideMobileNav={false} data-oid="n1-doql" />
      {/* Hero Section */}
      <section
        ref={heroSectionRef}
        id="home"
        className="relative min-h-[800px] sm:min-h-[900px] lg:h-screen flex items-center justify-center overflow-y-auto lg:overflow-hidden py-8 lg:py-0"
        data-oid="luoa9o7"
      >
        {/* Hero Background Video */}
        <video
          ref={heroVideoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            transition: "transform 0.1s ease-out",
          }}
          data-oid="kc0xu8h"
        >
          <source
            src="/videos/restaurant-hero-pc-720p-optimized.mp4"
            type="video/mp4"
            media="(min-width: 769px)"
            data-oid="lj0:aq:"
          />

          <source
            src="/videos/restaurant-hero-mobile-480p-optimized.mp4"
            type="video/mp4"
            media="(max-width: 768px)"
            data-oid="5oy-woy"
          />
        </video>

        <div
          className="relative z-10 flex items-center justify-center w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto my-auto"
          data-oid="lv2zvg0"
        >
          {/* Radial overlay behind logo */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            data-oid="4nspoha"
          >
            <div
              className="w-[400px] h-[400px] lg:w-[500px] lg:h-[500px] rounded-full opacity-60"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 70%)",
              }}
              data-oid="hw9xds5"
            />
          </div>

          {/* Restaurant Logo */}
          <div className="relative z-10 flex flex-col items-center justify-center" data-oid="_.-ouhv">
            {/* Semantic H1 for SEO - visually hidden but accessible */}
            <h1 className="sr-only" data-oid="90c0qt6">
              Restaurant Jungfrau - Fine Swiss Dining at Beatenberg Resort &
              Spa
            </h1>
            <img
              src="/images/Restaurant Jf.avif"
              alt={t("restaurant.hero.logoAlt")}
              className="h-48 sm:h-64 lg:h-72 w-auto"
              style={{
                filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.8))",
              }}
              data-oid="80yigzy"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="relative pt-16 sm:pt-32 pb-4 sm:pb-8 bg-gray-50 overflow-visible"
        data-oid="geakqpm"
      >
        {/* Background Text */}
        <div
          className="absolute inset-0 flex items-start justify-start pointer-events-none"
          data-oid="9qzs.o:"
        >
          <h2
            className="text-[8rem] sm:text-[12rem] md:text-[16rem] lg:text-[20rem] font-black text-gray-300/40 leading-none tracking-tighter transform translate-x-4 sm:translate-x-8 md:translate-x-12 -translate-y-4"
            data-oid="14k5kya"
          >
            {t("restaurant.about.backgroundText")}
          </h2>
        </div>

        <div
          className="container mx-auto px-6 relative z-10"
          data-oid="id8z2ea"
        >
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-20 items-center"
            data-oid="cxajngl"
          >
            {/* Left Side - Food Images */}
            <div
              className="relative flex items-center justify-center"
              data-oid="pp-ycwh"
            >
              {/* Main Salmon Plate */}
              <div className="relative z-10" data-oid="ofov3k1">
                <div
                  ref={imageRef}
                  className="w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] overflow-hidden"
                  style={{
                    animation: isImageVisible
                      ? "subtleRotate 1.5s ease-out forwards"
                      : "none",
                    opacity: isImageVisible ? 1 : 0,
                  }}
                  data-oid="z.r.4ck"
                >
                  <img
                    src="/images/alpine gastronomy.png"
                    alt="Alpine gastronomy"
                    className="w-full h-full object-contain"
                    data-oid="._ic8ij"
                  />
                </div>
              </div>

              {/* Floating Food Elements with Scroll Animation */}

              <div
                className="hidden sm:block absolute -bottom-12 -left-12 w-32 h-32 rounded-full overflow-hidden shadow-lg"
                style={{
                  transform: `rotate(${scrollY * 0.25}deg)`,
                  transition: "transform 0.1s ease-out",
                }}
                data-oid="het_8k2"
              >
                <img
                  src={restaurantHerbs}
                  alt="Fresh herbs"
                  className="w-full h-full object-cover"
                  data-oid=":-_ps2q"
                />
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="space-y-8 relative" data-oid="n::s89k">
              <div data-oid="w_np4su">
                <div className="flex items-center mb-6" data-oid="kfe0w3i">
                  <div
                    className="w-12 h-0.5 bg-red-500 mr-4"
                    data-oid="dfy07hd"
                  ></div>
                  <p
                    className="text-sm font-bold tracking-[0.3em] text-red-500"
                    data-oid="kh5-r5-"
                  >
                    {t("restaurant.about.tagline")}
                  </p>
                </div>

                <h2
                  className="font-playfair text-5xl font-black mb-8 leading-tight text-gray-900 uppercase tracking-tight"
                  data-oid="3ps.8lm"
                >
                  {t("restaurant.about.title")
                    .split("\n")
                    .map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index <
                          t("restaurant.about.title").split("\n").length -
                            1 && <br data-oid="tfbnk-x" />}
                      </React.Fragment>
                    ))}
                </h2>

                <p
                  className="text-gray-600 text-lg leading-relaxed mb-8"
                  data-oid="tp5es4s"
                >
                  {t("restaurant.about.description")}
                </p>
              </div>

              <div
                className="flex flex-col sm:flex-row gap-4 justify-center sm:justify-start mb-16"
                data-oid="2gihrgi"
              >
                <Button
                  variant="outline"
                  className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white px-8 py-4 font-bold tracking-widest uppercase flex items-center rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  data-oid="wx9emvh"
                >
                  <Phone className="mr-2 h-5 w-5" data-oid="44:uk1:" />
                  {t("restaurant.about.phoneButton")}
                </Button>
              </div>

              {/* Floating Pasta Bowl with Animation */}
              <div
                className="hidden sm:block absolute -top-8 -right-16 w-36 h-36 rounded-full overflow-hidden shadow-xl border-4 border-white"
                style={{
                  transform: `rotate(${scrollY * -0.1}deg)`,
                  transition: "transform 0.1s ease-out",
                }}
                data-oid="v3qdash"
              >
                <img
                  src={restaurantPastaBowl}
                  alt="Pasta dish"
                  className="w-full h-full object-cover"
                  data-oid="reyanlf"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        className="pb-16 bg-gray-50"
        style={{ paddingTop: "0px" }}
        data-oid="9.m69t0"
      >
        <div className="container mx-auto px-6" data-oid="cdg4dro">
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto pl-16 md:pl-0"
            data-oid="o-w5.uo"
          >
            {/* Alpine Views */}
            <div
              className="text-left md:text-center group cursor-pointer"
              data-oid="eo9w:ra"
            >
              <div
                className="flex items-start md:flex-col md:items-center gap-4 md:gap-0"
                data-oid="s43ptpu"
              >
                <div
                  className="w-12 h-12 md:w-16 md:h-16 border-2 border-gray-400 rounded-sm flex items-center justify-center group-hover:border-gray-600 transition-colors duration-300 flex-shrink-0 md:mb-6"
                  data-oid="uye2zzm"
                >
                  <MapPin
                    className="w-6 h-6 md:w-8 md:h-8 text-gray-400 group-hover:text-gray-600"
                    data-oid="eur8sa:"
                  />
                </div>
                <div className="text-left md:text-center" data-oid="vn.w82b">
                  <h3
                    className="font-playfair text-lg md:text-xl font-black mb-1 md:mb-2 text-gray-900 uppercase tracking-wide"
                    data-oid="b4:qsb2"
                  >
                    {t("restaurant.features.alpineViews.title")}
                  </h3>
                  <p className="text-gray-600 text-sm" data-oid="03p2-12">
                    {t("restaurant.features.alpineViews.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Local Sourcing */}
            <div
              className="text-left md:text-center group cursor-pointer"
              data-oid="d.5lpgm"
            >
              <div
                className="flex items-start md:flex-col md:items-center gap-4 md:gap-0"
                data-oid="yjuedf3"
              >
                <div
                  className="w-12 h-12 md:w-16 md:h-16 border-2 border-gray-400 rounded-full flex items-center justify-center group-hover:border-gray-600 transition-colors duration-300 flex-shrink-0 md:mb-6"
                  data-oid="t99z.qb"
                >
                  <Truck
                    className="w-5 h-5 md:w-6 md:h-6 text-gray-400 group-hover:text-gray-600"
                    data-oid="3vpaaz:"
                  />
                </div>
                <div className="text-left md:text-center" data-oid="pwsra.z">
                  <h3
                    className="font-playfair text-lg md:text-xl font-black mb-1 md:mb-2 text-gray-900 uppercase tracking-wide"
                    data-oid="f_qk8od"
                  >
                    {t("restaurant.features.localSourcing.title")}
                  </h3>
                  <p className="text-gray-600 text-sm" data-oid="m:6tjev">
                    {t("restaurant.features.localSourcing.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Swiss Heritage */}
            <div
              className="text-left md:text-center group cursor-pointer"
              data-oid="ws3hr4d"
            >
              <div
                className="flex items-start md:flex-col md:items-center gap-4 md:gap-0"
                data-oid="12:9pif"
              >
                <div
                  className="w-12 h-12 md:w-16 md:h-16 border-2 border-gray-400 rounded-sm flex items-center justify-center group-hover:border-gray-600 transition-colors duration-300 relative flex-shrink-0 md:mb-6"
                  data-oid="en7j3o7"
                >
                  <Shield
                    className="w-5 h-6 md:w-6 md:h-8 text-gray-400 group-hover:text-gray-600"
                    data-oid="it7r5is"
                  />
                </div>
                <div className="text-left md:text-center" data-oid="5:cfu6f">
                  <h3
                    className="font-playfair text-lg md:text-xl font-black mb-1 md:mb-2 text-gray-900 uppercase tracking-wide"
                    data-oid="_-sjd4u"
                  >
                    {t("restaurant.features.swissHeritage.title")}
                  </h3>
                  <p className="text-gray-600 text-sm" data-oid="0cjex9o">
                    {t("restaurant.features.swissHeritage.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section
        id="menu"
        className="pt-24 pb-12 bg-background relative overflow-hidden"
        data-oid="ze5m75i"
      >
        {/* Seamless Background Transition */}
        <div
          className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-gray-50 to-background pointer-events-none"
          data-oid="ju-uo_w"
        ></div>
        {/* Floating Food Elements */}
        <div
          className="hidden md:block absolute top-10 right-10 w-32 h-32 rounded-full overflow-hidden shadow-lg border-4 border-white"
          data-oid="w3ab:ru"
        >
          <img
            src={restaurantPasta}
            alt="Floating food"
            className="w-full h-full object-cover"
            data-oid="ye6uskn"
          />
        </div>
        <div
          className="hidden md:block absolute bottom-20 left-10 w-16 h-16 rounded-full overflow-hidden shadow-md"
          data-oid="wuw58w2"
        >
          <img
            src={restaurantIngredients}
            alt="Floating ingredient"
            className="w-full h-full object-cover"
            data-oid="zj:.6w."
          />
        </div>

        <div
          className="container mx-auto px-6 relative z-10"
          data-oid="bcmo40f"
        >
          <div className="text-center mb-16" data-oid="axk_.6d">
            <div
              className="flex items-center justify-center mb-6"
              data-oid="ytj6j0m"
            >
              <div
                className="w-8 h-0.5 bg-red-500 mr-4"
                data-oid="1rq:9xk"
              ></div>
              <p
                className="text-sm font-bold tracking-[0.3em] text-red-500"
                data-oid="nl7md1-"
              >
                {t("restaurant.menu.tagline")}
              </p>
              <div
                className="w-8 h-0.5 bg-red-500 ml-4"
                data-oid="sk9drvp"
              ></div>
            </div>
            <h2
              className="font-playfair text-5xl font-black text-gray-900 uppercase tracking-wide mb-4"
              data-oid="-8b.gx-"
            >
              {t("restaurant.menu.title")}
            </h2>
            <p
              className="text-gray-600 text-lg mb-6 max-w-3xl mx-auto leading-relaxed"
              data-oid="4kig7fz"
            >
              {t("restaurant.menu.description")}
            </p>
            <p className="text-gray-600 text-lg mb-8" data-oid="us35.le">
              {t("restaurant.menu.subtitle")}
            </p>
          </div>

          {/* Menu Tabs */}
          <div className="flex justify-center mb-16" data-oid="66vuv2.">
            <div className="flex space-x-12" data-oid="exna748">
              {Object.keys(menuItems).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex flex-col items-center space-y-2 px-4 py-3 transition-all duration-300 ${
                    activeTab === tab
                      ? "text-orange-500"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                  data-oid="apva4hw"
                >
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
                      activeTab === tab
                        ? "border-orange-500 bg-orange-50"
                        : "border-gray-300"
                    }`}
                    data-oid="3qga4kn"
                  >
                    {tab === "five_course_menu" && (
                      <ChefHat className="w-6 h-6" data-oid="5c4tvju" />
                    )}
                    {tab === "sides" && (
                      <UtensilsCrossed className="w-6 h-6" data-oid="89o4ey2" />
                    )}
                    {tab === "beverages" && (
                      <Coffee className="w-6 h-6" data-oid="3qcmtan" />
                    )}
                  </div>
                  <span
                    className="text-sm font-medium capitalize tracking-wide"
                    data-oid="paovgdn"
                  >
                    {tab === "five_course_menu" &&
                      t("restaurant.menu.tabs.fiveCourseDinner")}
                    {tab === "sides" && t("restaurant.menu.tabs.sides")}
                    {tab === "beverages" && t("restaurant.menu.tabs.beverages")}
                  </span>
                  {activeTab === tab && (
                    <div
                      className="w-6 h-0.5 bg-orange-500 rounded-full"
                      data-oid="jrc69o5"
                    ></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Menu Items - Dynamic Layout */}
          <div
            className={`${
              activeTab === "five_course_menu"
                ? "grid grid-cols-1 gap-8 max-w-5xl mx-auto"
                : "grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-8 max-w-6xl mx-auto"
            }`}
            data-oid="-ckyp.i"
          >
            {menuItems[activeTab as keyof typeof menuItems].map(
              (item, index) => (
                <div
                  key={index}
                  className={`${
                    activeTab === "five_course_menu"
                      ? "flex flex-col lg:flex-row items-center lg:items-center space-y-6 lg:space-y-0 lg:space-x-8 group bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl shadow-xl border border-orange-100"
                      : "flex items-center space-x-4 group"
                  }`}
                  data-oid="0pnqyw4"
                >
                  <div
                    className={`${
                      activeTab === "five_course_menu"
                        ? "w-56 h-56 lg:w-80 lg:h-80 rounded-2xl overflow-hidden shadow-2xl flex-shrink-0 order-2 lg:order-1"
                        : "w-20 h-20 rounded-full overflow-hidden shadow-lg flex-shrink-0"
                    }`}
                    data-oid="7rqf3cg"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      data-oid="a1tbszl"
                    />
                  </div>
                  <div
                    className={`${
                      activeTab === "five_course_menu" ? "flex-1 text-center lg:text-left order-1 lg:order-2" : "flex-1"
                    }`}
                    data-oid="0w8a2vp"
                  >
                    <div
                      className={`${
                        activeTab === "five_course_menu"
                          ? "flex flex-col lg:flex-row lg:justify-between lg:items-start mb-4"
                          : "flex justify-between items-start mb-2"
                      }`}
                      data-oid="klepnmo"
                    >
                      <h3
                        className={`${
                          activeTab === "five_course_menu"
                            ? "font-playfair text-2xl lg:text-3xl font-black text-gray-900 mb-2 lg:mb-0"
                            : "font-playfair text-lg font-bold text-gray-900"
                        }`}
                        data-oid="8oi8:cr"
                      >
                        {item.name}
                      </h3>
                      <span
                        className={`${
                          activeTab === "five_course_menu"
                            ? "text-2xl lg:text-3xl font-black text-red-500"
                            : "text-xl font-bold text-gray-900 ml-4"
                        }`}
                        data-oid="6207iiz"
                      >
                        {item.price}
                      </span>
                    </div>
                    <div
                      className={`${
                        activeTab === "five_course_menu"
                          ? "border-b-2 border-dotted border-orange-300 mb-4"
                          : "border-b border-dotted border-gray-300 mb-2"
                      }`}
                      data-oid="6fx:61p"
                    ></div>
                    {activeTab === "five_course_menu" ? (
                      <div className="text-sm lg:text-base text-gray-700 leading-relaxed space-y-3" data-oid="qyd062w">
                        {(() => {
                          const lines = item.description.split("|");
                          const course1 = lines.find(l => l.startsWith("COURSE_1:"));
                          const course2 = lines.find(l => l.startsWith("COURSE_2:"));
                          const course3 = lines.find(l => l.startsWith("COURSE_3:") && !l.includes("OPTION"));
                          const course3Options = lines.filter(l => l.startsWith("COURSE_3_OPTION"));
                          const course4 = lines.find(l => l.startsWith("COURSE_4:"));
                          const course5 = lines.find(l => l.startsWith("COURSE_5:"));
                          
                          return (
                            <>
                              {course1 && (
                                <div className="flex items-start space-x-3">
                                  <span className="font-bold text-red-500 text-xl mt-1 flex-shrink-0">1.</span>
                                  <div>
                                    <span className="font-semibold text-gray-900">{course1.replace("COURSE_1:", "").split(":")[0]}:</span>
                                    <span className="ml-2">{course1.replace("COURSE_1:", "").split(":")[1]}</span>
                                  </div>
                                </div>
                              )}
                              {course2 && (
                                <div className="flex items-start space-x-3">
                                  <span className="font-bold text-red-500 text-xl mt-1 flex-shrink-0">2.</span>
                                  <div>
                                    <span className="font-semibold text-gray-900">{course2.replace("COURSE_2:", "").split(":")[0]}:</span>
                                    <span className="ml-2">{course2.replace("COURSE_2:", "").split(":")[1]}</span>
                                  </div>
                                </div>
                              )}
                              {course3 && (
                                <div className="flex items-start space-x-3">
                                  <span className="font-bold text-red-500 text-xl mt-1 flex-shrink-0">3.</span>
                                  <div className="flex-1">
                                    <span className="font-semibold text-gray-900">{course3.replace("COURSE_3:", "")}</span>
                                    <div className="mt-2 ml-6 space-y-2">
                                      {course3Options.map((opt, optIdx) => {
                                        const optContent = opt.replace(/COURSE_3_OPTION_\d+:/, "");
                                        return (
                                          <div key={optIdx} className="text-gray-700 flex items-start">
                                            <span className="text-gray-900 mr-2 mt-1">•</span>
                                            <span>{optContent}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {course4 && (
                                <div className="flex items-start space-x-3">
                                  <span className="font-bold text-red-500 text-xl mt-1 flex-shrink-0">4.</span>
                                  <div>
                                    <span className="font-semibold text-gray-900">{course4.replace("COURSE_4:", "").split(":")[0]}:</span>
                                    <span className="ml-2">{course4.replace("COURSE_4:", "").split(":")[1]}</span>
                                  </div>
                                </div>
                              )}
                              {course5 && (
                                <div className="flex items-start space-x-3">
                                  <span className="font-bold text-red-500 text-xl mt-1 flex-shrink-0">5.</span>
                                  <div>
                                    <span className="font-semibold text-gray-900">{course5.replace("COURSE_5:", "").split(":")[0]}:</span>
                                    <span className="ml-2">{course5.replace("COURSE_5:", "").split(":")[1]}</span>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500" data-oid="qyd062w">
                        {item.description}
                      </p>
                    )}
                    <div
                      className={`${
                        activeTab === "five_course_menu"
                          ? "flex items-center justify-center lg:justify-start mt-4"
                          : "flex items-center mt-2"
                      }`}
                      data-oid="d5karva"
                    >
                      <div className="flex space-x-1" data-oid=".xre:rf">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`${
                              activeTab === "five_course_menu" ? "w-5 h-5" : "w-4 h-4"
                            } ${i < item.rating ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                            data-oid="3oscsno"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>

          {/* PDF Download and Restaurant Info */}
          <div className="text-center mt-16" data-oid="rkbmoz6">
            <div className="flex justify-center mb-4" data-oid="69uuinu">
              <Button
                variant="default"
                className="bg-red-500 text-white hover:bg-red-600 px-8 py-4 font-bold tracking-widest uppercase text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                onClick={() => window.open("/Jungfrau Menu.pdf", "_blank")}
                data-oid="kjma3v0"
              >
                {t("restaurant.menu.viewFullMenu")}
              </Button>
            </div>
            <div
              className="text-center text-sm text-gray-500"
              data-oid="os4caf6"
            >
              <p data-oid="02arbcv">{t("restaurant.menu.dinnerHours")}</p>
              <p data-oid=":1zsuq1">{t("restaurant.menu.pricesInclusive")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dining Venues Section - Full Screen Slider */}
      <section className="relative h-screen overflow-hidden" data-oid="4w5.wnd">
        {/* Slider Container */}
        <div className="relative h-full" data-oid="tm6u5gi">
          {diningVenues.map((venue, index) => (
            <div
              key={venue.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
              data-oid="7lepu5:"
            >
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${venue.bgImage})`,
                  transform: `scale(${index === currentSlide ? 1 : 1.1})`,
                  transition: "transform 10s ease-out",
                }}
                data-oid="nv3casn"
              />

              {/* Overlay */}
              <div
                className="absolute inset-0 bg-black/50"
                data-oid="736oicm"
              />

              {/* Content */}
              <div
                className="relative z-10 h-full flex items-center justify-center"
                data-oid="0kzs73i"
              >
                {/* Desktop/Tablet Rich Overlay */}
                <div
                  className="hidden md:block max-w-4xl mx-auto px-6 text-center text-white"
                  data-oid="hb.0lb5"
                >
                  {/* Title */}
                  <h3
                    className="font-playfair text-5xl md:text-6xl font-black mb-3 uppercase tracking-wide drop-shadow-lg"
                    data-oid="-p-wbxd"
                  >
                    {venue.title}
                  </h3>
                  {/* Subtitle */}
                  <h4
                    className="font-lato text-xl md:text-2xl font-bold mb-4 uppercase tracking-widest text-orange-300 drop-shadow-md"
                    data-oid="l-cm_90"
                  >
                    {venue.subtitle}
                  </h4>
                  {/* Description */}
                  <p
                    className="text-lg md:text-xl mb-8 leading-relaxed max-w-3xl mx-auto drop-shadow-md"
                    data-oid="8p1frq1"
                  >
                    {venue.description}
                  </p>
                  {/* Features */}
                  <div
                    className="grid grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto"
                    data-oid="r-m_clj"
                  >
                    {venue.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center justify-start"
                        data-oid="exmzwsi"
                      >
                        <div
                          className="w-2 h-2 bg-orange-400 rounded-full mr-3 flex-shrink-0"
                          data-oid="net9z76"
                        ></div>
                        <span
                          className="text-base text-white/90"
                          data-oid="ewx-:xn"
                        >
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  {/* Perfect For */}
                  <div
                    className="inline-block bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full"
                    data-oid="-5.szl5"
                  >
                    <span
                      className="text-orange-300 font-bold text-base"
                      data-oid="v1.ox:y"
                    >
                      Perfect for: {venue.perfectFor}
                    </span>
                  </div>
                </div>

                {/* Mobile Simplified Overlay */}
                <div className="md:hidden w-full px-4" data-oid="x4sfhdu">
                  <div className="mx-auto max-w-sm" data-oid="isyk2i9">
                    <div
                      className="bg-black/55 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
                      data-oid="h9vs32_"
                    >
                      <div className="mb-3" data-oid="d.io14x">
                        <h3
                          className="font-playfair text-2xl font-bold text-white leading-tight text-center"
                          data-oid="szlqeed"
                        >
                          {venue.title}
                        </h3>
                        <p
                          className="text-[10px] font-semibold uppercase tracking-widest text-orange-300 text-center"
                          data-oid="r.onrq2"
                        >
                          {venue.subtitle}
                        </p>
                      </div>
                      <p
                        className="text-white/90 text-[13px] leading-snug mb-3"
                        data-oid="vlag2g9"
                      >
                        {venue.description}
                      </p>
                      <div
                        className="flex flex-wrap gap-2 mb-3"
                        data-oid="v0dn5zz"
                      >
                        {venue.features
                          .slice(0, 3)
                          .map((feature, featureIndex) => (
                            <span
                              key={featureIndex}
                              className="text-[11px] text-white/90 bg-white/10 px-2.5 py-1 rounded-full"
                              data-oid="srze5d0"
                            >
                              {feature}
                            </span>
                          ))}
                      </div>
                      <div
                        className="text-[11px] text-orange-300 font-semibold"
                        data-oid="ulh.11:"
                      >
                        Perfect for: {venue.perfectFor}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
          data-oid="gb9pkr-"
        >
          <ArrowRight className="w-6 h-6 rotate-180" data-oid="ow.4f42" />
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
          data-oid="eg-wiax"
        >
          <ArrowRight className="w-6 h-6" data-oid="d692mgt" />
        </button>

        {/* Dots Navigation */}
        <div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3"
          data-oid="cf959.k"
        >
          {diningVenues.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? "bg-orange-500 scale-125"
                  : "bg-white/50 hover:bg-white/70"
              }`}
              data-oid="ep2vnt3"
            />
          ))}
        </div>

        {/* Slide Counter */}
        <div
          className="absolute bottom-8 right-8 z-20 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full"
          data-oid="9nu..0s"
        >
          <span className="text-white text-sm font-medium" data-oid="eywsv5_">
            {currentSlide + 1} / {diningVenues.length}
          </span>
        </div>
      </section>

      {/* Popular Dishes Section */}
      <section className="py-24 bg-gray-50" data-oid="d:a1rj7">
        {/* Header - Centered */}
        <div className="container mx-auto px-6" data-oid="6mynw_i">
          <div className="text-center mb-16" data-oid="xmz1gja">
            <div
              className="flex items-center justify-center mb-6"
              data-oid="iop0703"
            >
              <div
                className="w-8 h-0.5 bg-red-500 mr-4"
                data-oid="dfafo2:"
              ></div>
              <p
                className="text-sm font-bold tracking-[0.3em] text-red-500"
                data-oid="7tfac8a"
              >
                {t("restaurant.featuredItems.tagline")}
              </p>
              <div
                className="w-8 h-0.5 bg-red-500 ml-4"
                data-oid="2ikui8j"
              ></div>
            </div>
            <h2
              className="font-playfair text-5xl font-black text-gray-900 uppercase tracking-wide"
              data-oid="3ff.gfy"
            >
              {t("restaurant.featuredItems.title")}
            </h2>
          </div>
        </div>

        {/* Full-width Auto-scrolling Cards */}
        <div
          className="relative w-screen overflow-hidden"
          style={{
            marginLeft: "calc(-50vw + 50%)",
            marginRight: "calc(-50vw + 50%)",
          }}
          data-oid="f7a5w7_"
        >
          <div
            className="flex space-x-8 hover:pause-animation"
            style={{
              animation: "scrollDishes 20s linear infinite",
              width: `${featuredItems.length * 2 * 320}px`, // 320px per dish (w-80) + gap, duplicated
            }}
            data-oid="k9x9bb5"
          >
            {/* First set of dishes */}
            {featuredItems.map((dish, index) => (
              <div
                key={`first-${index}`}
                className="flex-shrink-0 w-80 group cursor-pointer"
                data-oid="eph:0c_"
              >
                <div
                  className="relative overflow-hidden rounded-lg shadow-lg mb-6"
                  data-oid="8q_b0ll"
                >
                  <img
                    src={dish.image}
                    alt={dish.name}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    data-oid="42qcikm"
                  />

                  <div
                    className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"
                    data-oid="rik8hwn"
                  ></div>
                  <div
                    className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold"
                    data-oid="6ihe-lm"
                  >
                    {dish.price}
                  </div>
                </div>
                <div className="text-center" data-oid="095:v50">
                  <h3
                    className="font-playfair text-xl font-bold text-gray-900 mb-2"
                    data-oid="d35ucch"
                  >
                    {dish.name}
                  </h3>
                  <p className="text-gray-500 text-sm" data-oid="ezeaypi">
                    {dish.ingredients}
                  </p>
                </div>
              </div>
            ))}
            {/* Duplicated set for seamless loop */}
            {featuredItems.map((dish, index) => (
              <div
                key={`second-${index}`}
                className="flex-shrink-0 w-80 group cursor-pointer"
                data-oid="0io0po-"
              >
                <div
                  className="relative overflow-hidden rounded-lg shadow-lg mb-6"
                  data-oid="l6ugtmm"
                >
                  <img
                    src={dish.image}
                    alt={dish.name}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    data-oid="3ddg71e"
                  />

                  <div
                    className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"
                    data-oid="vsknfwc"
                  ></div>
                  <div
                    className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold"
                    data-oid="u:6k8n-"
                  >
                    {dish.price}
                  </div>
                </div>
                <div className="text-center" data-oid="60gd5c9">
                  <h3
                    className="font-playfair text-xl font-bold text-gray-900 mb-2"
                    data-oid="m7cthil"
                  >
                    {dish.name}
                  </h3>
                  <p className="text-gray-500 text-sm" data-oid="4:5a:b8">
                    {dish.ingredients}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scrolling Text Section */}
      <section className="py-8 overflow-hidden relative" data-oid="ze:zc1d">
        {/* Split Background */}
        <div className="absolute inset-0 bg-gray-50" data-oid="kwf0455"></div>
        <div
          className="absolute inset-0 bg-orange-50"
          style={{ clipPath: "polygon(0 50%, 100% 50%, 100% 100%, 0 100%)" }}
          data-oid="o0k_6m:"
        ></div>

        <div className="relative z-10" data-oid="nz1e1:g">
          <div
            className="flex whitespace-nowrap scroll-text-mobile"
            style={{ animation: "scroll 30s linear infinite" }}
            data-oid="6vey-.h"
          >
            <div
              className="flex items-center space-x-16 text-8xl font-black"
              data-oid="u1mxmpf"
            >
              <span
                className="text-gray-200"
                style={{
                  WebkitTextStroke: "2px #d1d5db",
                  WebkitTextFillColor: "transparent",
                }}
                data-oid="lyhs7dt"
              >
                {t("restaurant.scrollingText.swissHeritage")}
              </span>
              <span className="text-gray-900" data-oid="b7bi0-c">
                {t("restaurant.scrollingText.alpineGastronomy")}
              </span>
              <span
                className="text-gray-200"
                style={{
                  WebkitTextStroke: "2px #d1d5db",
                  WebkitTextFillColor: "transparent",
                }}
                data-oid="s:7ivat"
              >
                {t("restaurant.scrollingText.farmToTable")}
              </span>
              <span className="text-gray-900" data-oid="65q7x81">
                {t("restaurant.scrollingText.fiveCourseDinner")}
              </span>
              <span
                className="text-gray-200"
                style={{
                  WebkitTextStroke: "2px #d1d5db",
                  WebkitTextFillColor: "transparent",
                }}
                data-oid="dcgf9ls"
              >
                {t("restaurant.scrollingText.swissWines")}
              </span>
              <span className="text-gray-900" data-oid="22s1cf6">
                {t("restaurant.scrollingText.localBeverages")}
              </span>
              <span
                className="text-gray-200"
                style={{
                  WebkitTextStroke: "2px #d1d5db",
                  WebkitTextFillColor: "transparent",
                }}
                data-oid="1uc:aw5"
              >
                {t("restaurant.scrollingText.alpineSides")}
              </span>
              <span className="text-gray-900" data-oid="3q1:-x7">
                {t("restaurant.scrollingText.freshSalads")}
              </span>
              <span
                className="text-gray-200"
                style={{
                  WebkitTextStroke: "2px #d1d5db",
                  WebkitTextFillColor: "transparent",
                }}
                data-oid="zaqoub."
              >
                {t("restaurant.scrollingText.gastrosuisseCertified")}
              </span>
              <span className="text-gray-900" data-oid="vgdd4s-">
                {t("restaurant.scrollingText.swissHeritage")}
              </span>
            </div>
          </div>
        </div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
            @keyframes scrollDishes {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @keyframes scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @media (max-width: 768px) {
              .scroll-text-mobile {
                animation: scroll 8s linear infinite !important;
              }
            }
            .hover\:pause-animation:hover {
              animation-play-state: paused;
            }
          `,
          }}
          data-oid="cssgjoy"
        />
      </section>

      {/* Testimonials Section */}
      <section
        className="py-24 bg-orange-50 relative overflow-hidden"
        data-oid="o7qk9ii"
      >
        <div
          className="container mx-auto px-6 relative z-10"
          data-oid="bg08qdc"
        >
          {/* Main Testimonial Content */}
          <div
            className="text-center max-w-4xl mx-auto relative"
            data-oid="jga0v4f"
          >
            {/* Quote Icon */}
            <div
              className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-8"
              data-oid="c.zu_pf"
            >
              <span
                className="text-white text-5xl font-bold"
                data-oid="vlu6kh2"
              >
                "
              </span>
            </div>

            {/* Current Testimonial */}
            <div className="mb-8" data-oid="-0n90rt">
              <blockquote
                className="text-xl font-medium text-gray-900 leading-relaxed mb-8 max-w-4xl mx-auto"
                data-oid="-anmosn"
              >
                "{testimonials[currentTestimonial].review}"
              </blockquote>
              <p
                className="text-orange-500 text-lg font-medium"
                data-oid="d9:hdep"
              >
                {testimonials[currentTestimonial].author}
              </p>
            </div>

            {/* Testimonial Navigation Images */}
            <div
              className="flex justify-center space-x-6 mb-12"
              data-oid="js4ajjb"
            >
              {testimonials.map((testimonial, index) => (
                <button
                  key={testimonial.id}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-16 h-16 rounded-full overflow-hidden border-4 shadow-lg transition-all duration-300 ${
                    index === currentTestimonial
                      ? "border-orange-500 scale-110"
                      : "border-white hover:border-orange-300"
                  }`}
                  data-oid="sn3e-c4"
                >
                  <img
                    src={testimonial.image}
                    alt={`Testimonial ${index + 1}`}
                    className="w-full h-full object-cover"
                    data-oid="vdrvdmw"
                  />
                </button>
              ))}
            </div>

            {/* Rating and Stats */}
          </div>

          {/* Floating Food Elements - Hidden on mobile */}
          <div
            className="hidden md:block absolute top-1/2 left-10 w-48 h-48 rounded-full overflow-hidden shadow-xl border-8 border-white transform -translate-y-1/2"
            data-oid="0d67n1e"
          >
            <img
              src="/images/breakfast_buffet.jpg"
              alt="Breakfast buffet"
              className="w-full h-full object-cover"
              data-oid="69i18yr"
            />

            {/* Small red circular element */}
            <div
              className="absolute top-8 right-12 w-6 h-6 bg-red-500 rounded-full border-2 border-white"
              data-oid="zqt_2q3"
            ></div>
          </div>

          <div
            className="hidden md:block absolute top-1/3 right-10 w-40 h-40 rounded-full overflow-hidden shadow-xl border-8 border-white"
            data-oid="8d1g8ji"
          >
            <img
              src="/images/Restaurant/IMG-20250812-WA0006.jpg"
              alt="Healthy experience"
              className="w-full h-full object-cover"
              data-oid="1a29b-x"
            />

            {/* Small green circular elements around the image */}
            <div
              className="absolute -top-3 -left-2 w-4 h-4 bg-green-500 rounded-full border border-white"
              data-oid="h6l5fwo"
            ></div>
            <div
              className="absolute -top-2 -right-3 w-3 h-3 bg-green-600 rounded-full border border-white"
              data-oid="7xp_cf3"
            ></div>
          </div>

          {/* Small Floating Elements - Hidden on mobile */}
          <div
            className="hidden md:block absolute top-16 left-1/4 w-8 h-8 bg-red-500 rounded-full"
            data-oid="c32b0eu"
          ></div>
          <div
            className="hidden md:block absolute bottom-20 right-1/4 w-6 h-6 bg-orange-500 rounded-full"
            data-oid=":pkgei."
          ></div>
          <div
            className="hidden md:block absolute top-32 right-1/3 w-4 h-4 bg-green-500 rounded-full"
            data-oid="3qmyz4l"
          ></div>
        </div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
            @keyframes scroll-fast {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `,
          }}
          data-oid="v8n0_es"
        />
      </section>

      <AutoScrollGallery
        id="gallery"
        eyebrow={t("restaurant.gallery.eyebrow")}
        title={t("restaurant.gallery.title")}
        subtitle={t("restaurant.gallery.subtitle")}
        images={[
          {
            src: "/images/rest page gallery/a_restaurant_with_a_row_of_tables_and_chairs_at_Pa_30.jpg",
            alt: "Restaurant dining area with tables and chairs",
          },
          {
            src: "/images/rest page gallery/a_group_of_people_in_a_kitchen_preparing_food_at_P_6.jpg",
            alt: "Chef team preparing food in kitchen",
          },
          {
            src: "/images/rest page gallery/a_plate_of_food_sitting_on_top_of_a_table_at_Parkh_240.jpg",
            alt: "Beautifully plated dish",
          },
          {
            src: "/images/rest page gallery/a_buffet_with_many_trays_of_food_on_a_table_at_Par_244.jpg",
            alt: "Buffet spread with various dishes",
          },
          {
            src: "/images/rest page gallery/a_restaurant_with_tables_and_chairs_and_a_tree_mur_3.jpg",
            alt: "Restaurant interior with elegant decor",
          },
          {
            src: "/images/rest page gallery/a_table_with_a_bunch_of_appetizers_and_salads_at_P_246.jpg",
            alt: "Appetizers and salads presentation",
          },
          {
            src: "/images/rest page gallery/IMG-20250813-WA0128.jpg",
            alt: "Restaurant ambiance and atmosphere",
          },
          {
            src: "/images/rest page gallery/a_person_is_preparing_food_on_a_counter_with_chees_60.jpg",
            alt: "Chef preparing food with fresh ingredients",
          },
          {
            src: "/images/rest page gallery/a_table_topped_with_three_plates_of_food_with_shri_243.jpg",
            alt: "Multiple dishes beautifully presented",
          },
          {
            src: "/images/rest page gallery/IMG-20250813-WA0171.jpg",
            alt: "Restaurant dining experience",
          },
          {
            src: "/images/rest page gallery/a_buffet_line_with_plates_of_shrimp_and_other_food_247.jpg",
            alt: "Buffet line with seafood and other dishes",
          },
        ]}
        showOverlayButton={false}
        scrollSpeed={40}
        imageHeight="h-[500px]"
        data-oid="7wa9_t0"
      />

      {/* GASTROSUISSE Certification Section */}
      <section
        className="py-24 bg-white relative overflow-hidden"
        data-oid="t9rm0z1"
      >
        {/* Seamless Background Transition */}
        <div
          className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-orange-50 to-white pointer-events-none"
          data-oid="7-36koo"
        ></div>
        <div className="container mx-auto px-6" data-oid="z9caihr">
          <div className="text-center mb-16" data-oid="lclbp53">
            <div
              className="flex items-center justify-center mb-6"
              data-oid="3.ca6r4"
            >
              <div
                className="w-8 h-0.5 bg-red-500 mr-4"
                data-oid="v1:8867"
              ></div>
              <p
                className="text-sm font-bold tracking-[0.3em] text-red-500"
                data-oid="l9zf1xs"
              >
                {t("restaurant.gastrosuisse.tagline")}
              </p>
              <div
                className="w-8 h-0.5 bg-red-500 ml-4"
                data-oid="rjmo6ck"
              ></div>
            </div>
            <h2
              className="font-playfair text-5xl font-black text-gray-900 uppercase tracking-wide mb-8"
              data-oid="qaw82:2"
            >
              {t("restaurant.gastrosuisse.title")}
            </h2>
            <p
              className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed"
              data-oid="su-7fhw"
            >
              {t("restaurant.gastrosuisse.description")}
            </p>
          </div>

          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto"
            data-oid="bd9qk4w"
          >
            {/* Left Side - Certification Details */}
            <div className="space-y-8" data-oid="65zz2r1">
              <div
                className="bg-gradient-to-r from-red-50 to-red-100 p-8 rounded-lg border-l-4 border-red-500"
                data-oid="20uk8xl"
              >
                <h3
                  className="font-playfair text-2xl font-black mb-4 text-gray-900 uppercase tracking-wide"
                  data-oid="7j51cra"
                >
                  {t("restaurant.gastrosuisse.certificationBenefits.title")}
                </h3>
                <ul className="space-y-3 text-gray-700" data-oid="2r38b2c">
                  {t(
                    "restaurant.gastrosuisse.certificationBenefits.benefits",
                  ).map((benefit, index) => (
                    <li
                      key={index}
                      className="flex items-start"
                      data-oid="._-18j."
                    >
                      <div
                        className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"
                        data-oid="wc5vjze"
                      ></div>
                      <span data-oid="vqucn5_">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div
                className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-lg border-l-4 border-gray-400"
                data-oid="8a_y7ed"
              >
                <h3
                  className="font-playfair text-2xl font-black mb-4 text-gray-900 uppercase tracking-wide"
                  data-oid="te-efb-"
                >
                  {t("restaurant.gastrosuisse.qualityStandards.title")}
                </h3>
                <ul className="space-y-3 text-gray-700" data-oid="k_xwnq6">
                  {t("restaurant.gastrosuisse.qualityStandards.standards").map(
                    (standard, index) => (
                      <li
                        key={index}
                        className="flex items-start"
                        data-oid="xqyfr.1"
                      >
                        <div
                          className="w-2 h-2 bg-gray-500 rounded-full mt-2 mr-3 flex-shrink-0"
                          data-oid="3q9_0ix"
                        ></div>
                        <span data-oid="1wavx3q">{standard}</span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>

            {/* Right Side - Certification Badge */}
            <div className="flex justify-center" data-oid="qveuf68">
              <div className="relative" data-oid="i4c6ror">
                {/* GASTROSUISSE Logo */}
                <div
                  className="w-80 h-80 flex flex-col items-center justify-center"
                  data-oid="_9wt67b"
                >
                  <img
                    src="/images/logo gastrosuisse.svg"
                    alt="GASTROSUISSE Logo"
                    className="w-64 h-64 object-contain mb-6"
                    data-oid="z-8z1ha"
                  />

                  {/* 4-Star Certification */}
                  <img
                    src="/images/gastroswiss_4_stern.webp"
                    alt="GASTROSUISSE 4-Star Certification"
                    className="w-80 h-auto object-contain"
                    data-oid="51-f5sn"
                  />
                </div>

                {/* Floating Elements */}
                <div
                  className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg"
                  data-oid="23w_dey"
                >
                  <Star
                    className="w-5 h-5 text-white fill-current"
                    data-oid=":bx1kcz"
                  />
                </div>
                <div
                  className="absolute -bottom-4 -left-4 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg"
                  data-oid="u8s5vei"
                >
                  <Shield className="w-4 h-4 text-white" data-oid="l1eb6yd" />
                </div>
                <div
                  className="absolute top-1/2 -left-8 w-4 h-4 bg-blue-500 rounded-full shadow-lg"
                  data-oid="2pvcb4c"
                ></div>
                <div
                  className="absolute top-1/2 -right-8 w-4 h-4 bg-purple-500 rounded-full shadow-lg"
                  data-oid="xun6iwr"
                ></div>
              </div>
            </div>
          </div>

          {/* Additional Accreditations */}
          <div className="mt-16 text-center" data-oid="jj54u0j">
            <h3
              className="font-playfair text-2xl font-black mb-8 text-gray-900 uppercase tracking-wide"
              data-oid="hncxfo4"
            >
              {t("restaurant.gastrosuisse.additionalAccreditations.title")}
            </h3>
            <div
              className="flex flex-wrap justify-center items-center gap-8"
              data-oid="bitj-18"
            >
              {t(
                "restaurant.gastrosuisse.additionalAccreditations.accreditations",
              ).map((accreditation, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-3 rounded-lg border-l-4 border-red-500"
                  data-oid="dalfl:o"
                >
                  <span
                    className="text-gray-800 font-medium"
                    data-oid="zjjh71d"
                  >
                    {accreditation}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-background" data-oid="q-__q1c">
        <div className="container mx-auto px-6" data-oid="ix4qqmc">
          <div className="text-center mb-16" data-oid="ihkv6un">
            <div
              className="flex items-center justify-center mb-6"
              data-oid="78wdb:p"
            >
              <div
                className="w-8 h-0.5 bg-orange-500 mr-4"
                data-oid=".4lkucj"
              ></div>
              <p
                className="text-sm font-bold tracking-[0.3em] text-orange-500"
                data-oid="46790bo"
              >
                {t("restaurant.contact.tagline")}
              </p>
              <div
                className="w-8 h-0.5 bg-orange-500 ml-4"
                data-oid="nu0.ent"
              ></div>
            </div>
            <h2
              className="font-playfair text-5xl font-bold mb-4"
              data-oid="9bbqn_k"
            >
              {t("restaurant.contact.title")}
            </h2>
            <p className="text-muted-foreground text-lg" data-oid="0ldjxqu">
              {t("restaurant.contact.subtitle")}
            </p>

            {/* Rating Display */}
            <div className="mt-6 flex justify-center" data-oid="eb4rei4">
              <div className="inline-block" data-oid="d45:ntr">
                <RatingDisplay
                  rating={4.6}
                  reviewCount={35}
                  type="restaurant"
                  theme="dark"
                  className="justify-center"
                  data-oid="7.rn2qd"
                />
              </div>
            </div>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto"
            data-oid="1w3p2lz"
          >
            <div className="text-center group" data-oid="1b7h:rw">
              <a
                href="https://www.google.com/maps/place/Restaurant+Jungfrau/@46.6976705,7.7765354,15z/data=!4m6!3m5!1s0x478fa5bd5f041421:0xd0898cc8ef8c20a7!8m2!3d46.6976705!4d7.7945598!16s%2Fg%2F11l2vxmgnc?entry=ttu&g_ep=EgoyMDI1MTAxNC4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Google Maps location"
                className="inline-block"
                data-oid="b4eldw2"
              >
                <div
                  className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-orange-600 transition-colors shadow-lg"
                  data-oid="dvr3m8i"
                >
                  <MapPin className="h-10 w-10 text-white" data-oid=".5tjop0" />
                </div>
              </a>
              <h3
                className="font-playfair text-2xl font-bold mb-4"
                data-oid="h:ma2sr"
              >
                {t("restaurant.contact.visitLocation.title")}
              </h3>
              <p
                className="text-muted-foreground text-lg leading-relaxed"
                data-oid="t:4-4us"
              >
                {t("restaurant.contact.visitLocation.address").map(
                  (line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index <
                        t("restaurant.contact.visitLocation.address").length -
                          1 && <br data-oid="a4zo90:" />}
                    </React.Fragment>
                  ),
                )}
              </p>
            </div>

            <div className="text-center group" data-oid="-59vmyi">
              <div
                className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-orange-600 transition-colors shadow-lg cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setIsContactModalOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setIsContactModalOpen(true);
                }}
                aria-label="Open contact options"
                data-oid="mchh9xk"
              >
                <Phone className="h-10 w-10 text-white" data-oid="0at97or" />
              </div>
              <h3
                className="font-playfair text-2xl font-bold mb-4"
                data-oid="k_9cqrp"
              >
                {t("restaurant.contact.callUs.title")}
              </h3>
              <p
                className="text-muted-foreground text-lg leading-relaxed"
                data-oid="95ogwjh"
              >
                {t("restaurant.contact.callUs.details").map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index <
                      t("restaurant.contact.callUs.details").length - 1 && (
                      <br data-oid="fnru.iy" />
                    )}
                  </React.Fragment>
                ))}
              </p>
            </div>

            <div className="text-center group" data-oid="8r5vvh_">
              <a
                href="mailto:info@beatenberg-resortspa.ch"
                aria-label="Email Restaurant Jungfrau"
                onClick={() =>
                  trackContactAction("concierge", "email", {
                    email_address: "info@beatenberg-resortspa.ch",
                    contact_source: "restaurant_direct",
                    page_location: "restaurant_contact_section",
                  })
                }
                className="contact-action-track inline-block"
                data-oid="a-ecv85"
              >
                <div
                  className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-orange-600 transition-colors shadow-lg"
                  data-oid=".zh92w_"
                >
                  <Mail className="h-10 w-10 text-white" data-oid="o-o:ln0" />
                </div>
              </a>
              <h3
                className="font-playfair text-2xl font-bold mb-4"
                data-oid="tqng2l-"
              >
                {t("restaurant.contact.email.title")}
              </h3>
              <p
                className="text-muted-foreground text-lg leading-relaxed"
                data-oid="0sk64yj"
              >
                {t("restaurant.contact.email.details").map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index <
                      t("restaurant.contact.email.details").length - 1 && (
                      <br data-oid="8_hwyyp" />
                    )}
                  </React.Fragment>
                ))}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer data-oid="lglyyua" />

      {/* Contact Concierge Modal */}
      <ContactConciergeModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        data-oid="gumak02"
      />
    </div>
  );
};

export default Restaurant;
